/**
 * chordRationalize.js
 *
 * Analyzes chord alignment against the song's section structure and applies
 * sequence-alignment-based correction. Sections of the same type (e.g., all
 * [Verse X] or all [Bridge] blocks) should share a consistent chord progression.
 *
 * Algorithm:
 *   1. Parse section headers and group like-type sections
 *   2. Extract an ordered chord sequence from each instance
 *   3. Pick the most-complete instance as the reference
 *   4. DP-align (Needleman-Wunsch) every other instance to the reference
 *   5. Build a consensus at each aligned column (majority vote across all instances)
 *   6. For each instance, generate corrections:
 *        - Substitution: wrong chord at a column → replace with consensus
 *          EXCEPTION: if the outlier is harmonically related to the consensus
 *          (relative major/minor, same root different quality, enharmonic) it may
 *          be an intentional arrangement variation — preserve it unchanged.
 *        - Deletion in instance: instance has gap where consensus has chord → add chord
 *        - Insertion in instance: instance has chord where consensus has gap → remove chord
 */

// ---------------------------------------------------------------------------
// Section type normalisation
// ---------------------------------------------------------------------------

const SECTION_TYPE_RULES = [
  { type: 'verse',     rx: /^verse|^v[\s.]?\d/i },
  { type: 'chorus',    rx: /^chorus|^refrain|^ch/i },
  { type: 'prechorus', rx: /^pre[\s-]?chorus|^pre/i },
  { type: 'bridge',    rx: /^bridge/i },
  { type: 'outro',     rx: /^outro|^coda|^ending|^tag/i },
  { type: 'intro',     rx: /^intro/i },
];

function normalizeSectionType(name) {
  const n = name.trim();
  for (const { type, rx } of SECTION_TYPE_RULES) {
    if (rx.test(n)) return type;
  }
  return n.toLowerCase().replace(/\s*\d+\s*$/, '').trim() || 'unknown';
}

function parseSections(lines) {
  const sections = [];
  let current = null;
  for (let li = 0; li < lines.length; li++) {
    const match = lines[li].trim().match(/^\[(.+)\]$/);
    if (match) {
      if (current) current.endLine = li - 1;
      current = {
        name: match[1],
        type: normalizeSectionType(match[1]),
        headerLine: li,
        startLine: li + 1,
        endLine: lines.length - 1,
      };
      sections.push(current);
    }
  }
  return sections;
}

// ---------------------------------------------------------------------------
// Music-theory helpers
// ---------------------------------------------------------------------------

const NOTE_TO_PC = {
  C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4,
  F: 5, 'F#': 6, Gb: 6, G: 7, 'G#': 8, Ab: 8,
  A: 9, 'A#': 10, Bb: 10, B: 11, Cb: 11,
};

function chordRoot(chord) {
  if (!chord) return '';
  return (chord.length >= 2 && (chord[1] === '#' || chord[1] === 'b'))
    ? chord.slice(0, 2)
    : chord.slice(0, 1);
}

function coreQuality(chord) {
  const c = String(chord || '').trim();
  const r = chordRoot(c);
  const q = c.slice(r.length).toLowerCase();
  if (!q) return 'maj';
  if (q.includes('sus')) return q.includes('sus2') ? 'sus2' : 'sus';
  if (q.includes('dim')) return 'dim';
  if (q.includes('aug') || q.includes('+')) return 'aug';
  if (q.includes('maj')) return 'maj';
  // "m" quality, but not major marker
  if (q.startsWith('m') && !q.startsWith('maj')) return 'min';
  return 'maj';
}

function chordFamilyKey(chord) {
  const r = chordRoot(chord);
  if (!r) return '';
  return `${r}|${coreQuality(chord)}`;
}

function simplifyForConsensus(chord) {
  const c = String(chord || '').trim();
  const r = chordRoot(c);
  if (!r) return c;
  const q = coreQuality(c);
  if (q === 'min') return `${r}m`;
  if (q === 'sus2') return `${r}sus2`;
  if (q === 'sus') return `${r}sus`;
  if (q === 'dim') return `${r}dim`;
  if (q === 'aug') return `${r}aug`;
  return r;
}

function pickFamilyRepresentative(chords = [], familyKey = '') {
  const inFamily = (chords || []).filter((c) => chordFamilyKey(c) === familyKey);
  if (!inFamily.length) return null;
  const counts = {};
  inFamily.forEach((c) => {
    const s = simplifyForConsensus(c);
    counts[s] = (counts[s] || 0) + 1;
  });
  return Object.entries(counts).sort(([, a], [, b]) => b - a)[0][0];
}

/** Alignment score for two chords: higher = more likely the same harmonic position. */
function chordAlignScore(a, b) {
  if (a === b) return 3;
  const rA = chordRoot(a), rB = chordRoot(b);
  if (rA === rB) return 2; // same root, different quality (e.g. Gm vs Gm7)
  const pcA = NOTE_TO_PC[rA] ?? -1, pcB = NOTE_TO_PC[rB] ?? -1;
  if (pcA >= 0 && pcB >= 0) {
    if (pcA === pcB) return 2; // enharmonic equivalents
    const diff = Math.min(Math.abs(pcA - pcB), 12 - Math.abs(pcA - pcB));
    if (diff === 1) return 1;  // 1 semitone
    if (diff === 2) return 0;
  }
  return -2;
}

/**
 * Rationalization should normalize section repeats aggressively.
 * The only variation we preserve is pure enharmonic spelling (C# vs Db),
 * since it is musically identical and mostly notation preference.
 */
function isIntentionalVariation(outlier, consensus) {
  if (!outlier || !consensus || outlier === consensus) return false;
  const rA = chordRoot(outlier), rB = chordRoot(consensus);
  const pcA = NOTE_TO_PC[rA] ?? -1, pcB = NOTE_TO_PC[rB] ?? -1;
  if (pcA < 0 || pcB < 0) return false;
  if (pcA !== pcB) return false;
  // Preserve only spelling differences (e.g., C# vs Db), not quality/function changes (C vs C7).
  if (rA === rB) return false;
  const qa = outlier.slice(rA.length).toLowerCase();
  const qb = consensus.slice(rB.length).toLowerCase();
  return qa === qb;
}

// ---------------------------------------------------------------------------
// Chord sequence extraction
// ---------------------------------------------------------------------------

function extractChordSequence(chordMap, lines, sec) {
  const seq = [];
  let relLine = 0;
  for (let li = sec.startLine; li <= sec.endLine; li++) {
    const trimmed = lines[li]?.trim();
    if (!trimmed) continue;
    if (/^\[.+\]/.test(trimmed)) break;
    if (Array.isArray(chordMap[li]) && chordMap[li].length) {
      const events = chordMap[li]
        .slice()
        .sort((a, b) => {
          const ta = Number.isFinite(Number(a.time)) ? Number(a.time) : Number.POSITIVE_INFINITY;
          const tb = Number.isFinite(Number(b.time)) ? Number(b.time) : Number.POSITIVE_INFINITY;
          if (ta !== tb) return ta - tb;
          if (Number(a.wordIndex) !== Number(b.wordIndex)) return Number(a.wordIndex) - Number(b.wordIndex);
          return Number(a.syllableOffset || 0) - Number(b.syllableOffset || 0);
        });
      for (const evt of events) {
        seq.push({
          chord: evt.chord,
          relLine,
          wordIndex: Number(evt.wordIndex),
          syllableOffset: Number(evt.syllableOffset || 0),
          absLine: li,
          time: evt.time,
        });
      }
    }
    relLine++;
  }
  return seq;
}

function getAbsLineForRelLine(lines, sec, targetRelLine) {
  let rl = 0;
  for (let li = sec.startLine; li <= sec.endLine; li++) {
    const t = lines[li]?.trim();
    if (!t || /^\[.+\]/.test(t)) continue;
    if (rl === targetRelLine) return li;
    rl++;
  }
  return -1;
}

/** Count the lyric words on a given absolute line. */
function countLineWords(lines, absLine) {
  const t = lines[absLine]?.trim();
  if (!t || /^\[.+\]/.test(t)) return 0;
  return t.split(/\s+/).filter(Boolean).length;
}

/**
 * Given a reference chord position and Whisper timing data, find the best
 * word index in the target line.
 *
 * Strategy (with timings):
 *   1. Look up the start-time of the reference word.
 *   2. Compute its fractional position within its line's time span.
 *   3. Map fraction to a target time within the target line's span.
 *   4. If a beats grid is available, snap that target time to the nearest beat
 *      so the chord lands on a musical position rather than mid-beat.
 *   5. Find the word in the target line whose start-time is closest.
 *
 * Fallback (no timings):
 *   Simple proportional mapping by word count.
 */
function bestWordIndex(refWordIndex, refAbsLine, targetAbsLine, lines, timingMap, beats = []) {
  const refLineTimings  = timingMap[refAbsLine];
  const tgtLineTimings  = timingMap[targetAbsLine];

  if (refLineTimings && tgtLineTimings) {
    // Collect sorted word entries for each line
    const refWords = Object.keys(refLineTimings).map(Number).sort((a, b) => a - b);
    const tgtWords = Object.keys(tgtLineTimings).map(Number).sort((a, b) => a - b);

    if (refWords.length > 0 && tgtWords.length > 0) {
      // Time span of the reference line
      const refLineStart = refLineTimings[refWords[0]].start ?? 0;
      const refLineEnd   = refLineTimings[refWords[refWords.length - 1]].end
                        ?? refLineTimings[refWords[refWords.length - 1]].start ?? refLineStart;

      const refWordTime = refLineTimings[refWordIndex]?.start ?? refLineStart;
      const refDuration = Math.max(refLineEnd - refLineStart, 0.001);
      const fraction    = Math.min(Math.max((refWordTime - refLineStart) / refDuration, 0), 1);

      // Time span of the target line
      const tgtLineStart = tgtLineTimings[tgtWords[0]].start ?? 0;
      const tgtLineEnd   = tgtLineTimings[tgtWords[tgtWords.length - 1]].end
                        ?? tgtLineTimings[tgtWords[tgtWords.length - 1]].start ?? tgtLineStart;
      const tgtDuration  = Math.max(tgtLineEnd - tgtLineStart, 0.001);

      let targetTime = tgtLineStart + fraction * tgtDuration;

      // Snap to nearest beat within the target line's time window
      if (beats.length > 0) {
        const lineBeats = beats.filter(b => b.time >= tgtLineStart - 0.5 && b.time <= tgtLineEnd + 0.5);
        if (lineBeats.length > 0) {
          let nearestBeat = lineBeats[0];
          let bestDist = Math.abs(lineBeats[0].time - targetTime);
          for (const b of lineBeats) {
            const d = Math.abs(b.time - targetTime);
            if (d < bestDist) { bestDist = d; nearestBeat = b; }
          }
          targetTime = nearestBeat.time;
        }
      }

      // Find the target word whose start time is closest to targetTime
      let bestWi = tgtWords[0];
      let bestDist = Infinity;
      for (const wi of tgtWords) {
        const t = tgtLineTimings[wi].start ?? tgtLineTimings[wi].end ?? 0;
        const dist = Math.abs(t - targetTime);
        if (dist < bestDist) { bestDist = dist; bestWi = wi; }
      }
      return bestWi;
    }
  }

  // Fallback: proportional by word count
  const refCount = countLineWords(lines, refAbsLine);
  const tgtCount = countLineWords(lines, targetAbsLine);
  if (refCount <= 1 || tgtCount <= 1) return Math.min(refWordIndex, Math.max(tgtCount - 1, 0));
  const ratio = refWordIndex / (refCount - 1);
  return Math.min(Math.round(ratio * (tgtCount - 1)), tgtCount - 1);
}

// ---------------------------------------------------------------------------
// Needleman-Wunsch alignment of two chord sequences
// ---------------------------------------------------------------------------

const GAP_PENALTY = -1;

function alignmentPositionBias(refItem, instItem) {
  if (!refItem || !instItem) return 0;
  const lineDist = Math.abs(Number(refItem.relLine) - Number(instItem.relLine));
  const wordDist = Math.abs(Number(refItem.wordIndex) - Number(instItem.wordIndex));
  let bias = 0;

  // Enforce same-relative-line alignment. Cross-line matches are disallowed
  // so extra chords on one line cannot consume pattern slots from other lines.
  if (lineDist !== 0) return -100;
  bias += 1.2;

  // Within-line ordering: reward close word positions, penalize large drift.
  if (lineDist === 0) {
    if (wordDist <= 1) bias += 0.35;
    else bias -= Math.min(1.5, (wordDist - 1) * 0.4);
  }
  return bias;
}

function alignChordSequences(refSeq, instSeq) {
  const n = refSeq.length, m = instSeq.length;
  const dp = Array.from({ length: n + 1 }, () => new Float32Array(m + 1));
  const tr = Array.from({ length: n + 1 }, () => new Uint8Array(m + 1));

  for (let i = 1; i <= n; i++) { dp[i][0] = i * GAP_PENALTY; tr[i][0] = 2; }
  for (let j = 1; j <= m; j++) { dp[0][j] = j * GAP_PENALTY; tr[0][j] = 3; }

  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      const score = chordAlignScore(refSeq[i - 1].chord, instSeq[j - 1].chord)
        + alignmentPositionBias(refSeq[i - 1], instSeq[j - 1]);
      const diag = dp[i - 1][j - 1] + score;
      const up   = dp[i - 1][j] + GAP_PENALTY;
      const left = dp[i][j - 1] + GAP_PENALTY;
      if (diag >= up && diag >= left) { dp[i][j] = diag; tr[i][j] = 1; }
      else if (up >= left)            { dp[i][j] = up;   tr[i][j] = 2; }
      else                            { dp[i][j] = left; tr[i][j] = 3; }
    }
  }

  const pairs = [];
  let i = n, j = m;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && tr[i][j] === 1) {
      pairs.unshift({ ref: refSeq[i - 1], inst: instSeq[j - 1] }); i--; j--;
    } else if (i > 0 && tr[i][j] === 2) {
      pairs.unshift({ ref: refSeq[i - 1], inst: null }); i--;
    } else {
      pairs.unshift({ ref: null, inst: instSeq[j - 1] }); j--;
    }
  }
  return pairs;
}

// ---------------------------------------------------------------------------
// Rationalize a single section type group
// ---------------------------------------------------------------------------

function rationalizeGroup(instances, chordMap, lines, timingMap, beats = []) {
  const corrections = {};
  const additions   = [];
  const deletions   = new Set();

  const allSeqs = instances.map(sec => ({
    sec,
    seq: extractChordSequence(chordMap, lines, sec),
  }));
  const seqBySectionHeader = new Map(
    allSeqs.map(({ sec, seq }) => [Number(sec.headerLine), seq])
  );
  const debug = {
    referenceIndex: null,
    referenceSection: null,
    referenceSequence: [],
    instanceSequences: allSeqs.map(({ sec, seq }, idx) => ({
      index: idx,
      name: sec.name,
      headerLine: sec.headerLine,
      startLine: sec.startLine,
      seq: seq.map((s) => ({
        relLine: s.relLine,
        wordIndex: s.wordIndex,
        chord: s.chord,
        absLine: s.absLine,
      })),
    })),
    summary: {
      corrections: 0,
      additions: 0,
      deletions: 0,
    },
    consensusColumns: [],
    consensusTemplate: [],
    lineConsensusTemplates: [],
  };
  if (!allSeqs.some(({ seq }) => seq.length)) return { corrections, additions, deletions, debug };

  const sectionHasFamilyOnRelLine = (sec, relLine, chord, ignoreAbsLine = null, ignoreWordIndex = null) => {
    const fam = chordFamilyKey(chord);
    if (!fam) return false;
    const seq = seqBySectionHeader.get(Number(sec?.headerLine)) || [];
    return seq.some((s) => {
      if (Number(s.relLine) !== Number(relLine)) return false;
      if (ignoreAbsLine != null && ignoreWordIndex != null
        && Number(s.absLine) === Number(ignoreAbsLine)
        && Number(s.wordIndex) === Number(ignoreWordIndex)) {
        return false;
      }
      return chordFamilyKey(s.chord) === fam;
    });
  };

  const N = allSeqs.length;

  const relLines = [...new Set(
    allSeqs.flatMap(({ seq }) => seq.map((s) => Number(s.relLine)))
  )].sort((a, b) => a - b);

  const chooseRefIdxForLine = (lineSeqs) => {
    let bestIdx = -1;
    let best = Number.NEGATIVE_INFINITY;
    for (let i = 0; i < lineSeqs.length; i++) {
      if (!lineSeqs[i].seq.length) continue;
      let total = 0;
      for (let j = 0; j < lineSeqs.length; j++) {
        if (i === j || !lineSeqs[j].seq.length) continue;
        const pairs = alignChordSequences(lineSeqs[i].seq, lineSeqs[j].seq);
        for (const p of pairs) total += (p.ref && p.inst) ? chordAlignScore(p.ref.chord, p.inst.chord) : GAP_PENALTY;
      }
      if (total > best) { best = total; bestIdx = i; }
    }
    return bestIdx >= 0 ? bestIdx : 0;
  };

  relLines.forEach((relLine) => {
    const lineSeqs = allSeqs.map(({ sec, seq }, instIdx) => ({
      instIdx,
      sec,
      seq: seq.filter((s) => Number(s.relLine) === relLine).sort((a, b) => Number(a.wordIndex) - Number(b.wordIndex)),
    }));
    if (!lineSeqs.some((x) => x.seq.length)) return;

    const refLineIdx = chooseRefIdxForLine(lineSeqs);
    const refLineSeq = lineSeqs[refLineIdx].seq;
    if (!refLineSeq.length) return;

    // Flattened reference/consensus diagnostics for UI logging.
    refLineSeq.forEach((r) => {
      debug.referenceSequence.push({
        relLine: r.relLine,
        wordIndex: r.wordIndex,
        chord: r.chord,
        absLine: r.absLine,
      });
    });

    const aligned = lineSeqs.map(({ sec, seq, instIdx }, i) => ({
      sec,
      instIdx,
      pairs: i === refLineIdx
        ? refLineSeq.map((r) => ({ ref: r, inst: r }))
        : alignChordSequences(refLineSeq, seq),
    }));

    const refCols = refLineSeq.map(() => []);
    const insertsBefore = Array.from({ length: refLineSeq.length + 1 }, () => []);
    aligned.forEach(({ sec, instIdx, pairs }) => {
      let ri = 0;
      pairs.forEach(({ ref, inst }) => {
        if (ref) {
          refCols[ri].push({ chord: inst ? inst.chord : null, instItem: inst || null, sec, instIdx });
          ri += 1;
        } else if (inst) {
          insertsBefore[ri].push({ chord: inst.chord, instItem: inst, sec, instIdx });
        }
      });
      while (ri < refLineSeq.length) {
        refCols[ri].push({ chord: null, instItem: null, sec, instIdx });
        ri += 1;
      }
    });

    const lineTemplate = [];
    for (let ri = 0; ri < refLineSeq.length; ri++) {
      const col = refCols[ri];
      const votes = {};
      const concrete = [];
      col.forEach(({ chord }) => {
        const k = chord == null ? '__gap__' : chordFamilyKey(chord);
        votes[k] = (votes[k] || 0) + 1;
        if (chord) concrete.push(chord);
      });
      const chordOnly = Object.entries(votes).filter(([k]) => k !== '__gap__' && k).sort(([, a], [, b]) => b - a);
      const [topFamily, topFamilyCount] = chordOnly[0] || [null, 0];
      const nonGapTotal = chordOnly.reduce((sum, [, c]) => sum + c, 0);
      const nonGapShare = nonGapTotal > 0 ? (topFamilyCount / nonGapTotal) : 0;
      const sorted = Object.entries(votes).sort(([, a], [, b]) => b - a);
      const [winner, winCount] = sorted[0];
      const hasStrictMajority = winCount > N / 2;
      const consensusChord = winner === '__gap__' ? null : pickFamilyRepresentative(concrete, winner);
      const hasStrongNonGap = Boolean(topFamily && topFamilyCount >= 2 && nonGapShare >= 0.6);

      debug.consensusColumns.push({
        refIndex: ri,
        refChord: refLineSeq[ri]?.chord ?? null,
        refRelLine: relLine,
        refWordIndex: refLineSeq[ri]?.wordIndex ?? null,
        winner,
        winCount,
        instanceCount: N,
        supportRatio: Number((winCount / Math.max(1, N)).toFixed(3)),
        consensusChord,
        hasStrictMajority,
        topChord: topFamily ? pickFamilyRepresentative(concrete, topFamily) : null,
        topChordCount: topFamilyCount,
        nonGapTotal,
        nonGapShare: Number(nonGapShare.toFixed(3)),
      });

      if (!hasStrictMajority && !hasStrongNonGap) continue;
      if (consensusChord) {
        lineTemplate.push({
          refIndex: ri,
          relLine,
          wordIndex: refLineSeq[ri]?.wordIndex ?? null,
          chord: consensusChord,
          winCount,
          instanceCount: N,
        });
        debug.consensusTemplate.push({
          refIndex: ri,
          relLine,
          wordIndex: refLineSeq[ri]?.wordIndex ?? null,
          chord: consensusChord,
          winCount,
          instanceCount: N,
        });
      }

      col.forEach(({ chord, instItem, sec }) => {
        const targetChordForSubst = hasStrongNonGap
          ? pickFamilyRepresentative(concrete, topFamily)
          : consensusChord;
        if (chord === consensusChord || chord === targetChordForSubst) return;

        if (hasStrictMajority && consensusChord === null) {
          // Don't delete pre-word spacers (wordIndex < 0): these are chords detected
          // in the gap before a line starts and are musically valid even if other
          // section instances don't have them.
          if (instItem && Number(instItem.wordIndex) >= 0) {
            deletions.add(`${instItem.absLine}:${instItem.wordIndex}`);
          }
          return;
        }

        if (hasStrictMajority && chord === null) {
          const refItem = refLineSeq[ri];
          const targetAbsLine = getAbsLineForRelLine(lines, sec, relLine);
          if (targetAbsLine >= 0 && !sectionHasFamilyOnRelLine(sec, relLine, consensusChord)) {
            const refWi = Number.isFinite(Number(refItem.wordIndex))
              ? Math.max(0, Math.round(Number(refItem.wordIndex)))
              : 0;
            const wi = bestWordIndex(refWi, refItem.absLine, targetAbsLine, lines, timingMap, beats);
            additions.push({ absLine: targetAbsLine, wordIndex: wi, chord: consensusChord });
          }
          return;
        }

        const subTarget = targetChordForSubst;
        if (subTarget && !isIntentionalVariation(chord, subTarget) && instItem) {
          corrections[`${instItem.absLine}:${instItem.wordIndex}`] = subTarget;
        }
      });
    }
    debug.lineConsensusTemplates.push({ relLine, template: lineTemplate });

    // Insertion handling for this relative line.
    for (let ri = 0; ri <= refLineSeq.length; ri++) {
      const inserts = insertsBefore[ri];
      if (!inserts.length) continue;

      if (inserts.length <= N / 2) {
        inserts.forEach(({ instItem }) => {
          // Don't delete pre-word spacers (wordIndex < 0): chords detected in the gap
          // before a line start are valid musical events even in minority instances.
          if (instItem && Number(instItem.wordIndex) >= 0) {
            deletions.add(`${instItem.absLine}:${instItem.wordIndex}`);
          }
        });
        continue;
      }

      const votes = {};
      inserts.forEach(({ chord }) => { votes[chordFamilyKey(chord)] = (votes[chordFamilyKey(chord)] || 0) + 1; });
      const [insertFamily, insertVotes] = Object.entries(votes).sort(([, a], [, b]) => b - a)[0] || [null, 0];
      if (!insertFamily || insertVotes <= N / 2) continue;
      const insertChord = pickFamilyRepresentative(inserts.map((x) => x.chord), insertFamily);
      if (!insertChord) continue;

      const anchorVotes = {};
      inserts.forEach((ins) => {
        const wi = Number(ins?.instItem?.wordIndex);
        if (!Number.isInteger(wi) || wi < 0) return;
        anchorVotes[wi] = (anchorVotes[wi] || 0) + 1;
      });
      const [anchorWordStr, anchorCount] = Object.entries(anchorVotes).sort(([, a], [, b]) => b - a)[0] || [null, 0];
      if (anchorWordStr == null || anchorCount <= N / 2) continue;
      const anchorWordIndex = Number(anchorWordStr);
      const anchorSample = inserts.find((ins) => Number(ins?.instItem?.wordIndex) === anchorWordIndex)?.instItem;
      const hasInsert = new Set(inserts.map((ins) => ins.instIdx));

      lineSeqs.forEach(({ instIdx, sec }) => {
        if (hasInsert.has(instIdx)) return;
        const targetAbsLine = getAbsLineForRelLine(lines, sec, relLine);
        if (targetAbsLine >= 0 && !sectionHasFamilyOnRelLine(sec, relLine, insertChord)) {
          const wi = bestWordIndex(
            anchorWordIndex,
            anchorSample?.absLine ?? targetAbsLine,
            targetAbsLine,
            lines,
            timingMap,
            beats
          );
          additions.push({ absLine: targetAbsLine, wordIndex: wi, chord: insertChord });
        }
      });
    }
  });

  // Best-effort global reference metadata for existing UI logs.
  debug.referenceIndex = 0;
  debug.referenceSection = instances[0]
    ? {
        name: instances[0].name,
        headerLine: instances[0].headerLine,
        startLine: instances[0].startLine,
      }
    : null;

  debug.summary = {
    corrections: Object.keys(corrections).length,
    additions: additions.length,
    deletions: deletions.size,
  };
  return { corrections, additions, deletions, debug };
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Apply sequence-alignment-based rationalization to a chord alignment.
 *
 * @param {Array<{chord, lineIndex, wordIndex}>} alignment
 * @param {string} lyrics      — full lyrics text with [Section] markers
 * @param {string} songKey     — detected key (reserved for future use)
 * @param {Array<{lineIndex, wordIndex, start, end}>} [timedLyrics]
 *   — Whisper word timings from computeTimedLyrics(). Used to find the best
 *     word position by actual time when inserting a missing chord.
 * @param {Array<{time, measure, beat}>} [beats]
 *   — Beat grid from madmom. Used to snap chord insertions to musical beat positions.
 * @returns {{ alignment: Array, corrections: number }}
 */
export function rationalizeAlignment(alignment, lyrics, songKey, timedLyrics = [], beats = []) {
  if (!alignment || alignment.length === 0) return { alignment, corrections: 0, diagnostics: {} };

  const lines = lyrics.split('\n');
  const sections = parseSections(lines);
  if (sections.length === 0) return { alignment, corrections: 0, diagnostics: {} };

  // Build timing map: timingMap[lineIndex][wordIndex] = { start, end }
  const timingMap = {};
  for (const { lineIndex, wordIndex, start, end } of (timedLyrics || [])) {
    if (lineIndex == null || lineIndex < 0) continue;
    if (start == null && end == null) continue;
    if (!timingMap[lineIndex]) timingMap[lineIndex] = {};
    timingMap[lineIndex][wordIndex] = { start, end };
  }

  const chordMap = {};
  for (const { chord, lineIndex, wordIndex, syllableOffset = 0, time = null } of alignment) {
    if (lineIndex < 0) continue;
    if (!Number.isFinite(Number(wordIndex))) continue;
    if (!chordMap[lineIndex]) chordMap[lineIndex] = [];
    chordMap[lineIndex].push({
      chord,
      wordIndex: Number(wordIndex),
      syllableOffset: Number(syllableOffset || 0),
      time,
    });
  }

  const byType = {};
  for (const sec of sections) {
    if (!byType[sec.type]) byType[sec.type] = [];
    byType[sec.type].push(sec);
  }

  const allCorrections = {};
  const allAdditions   = [];
  const allDeletions   = new Set();
  const diagnostics = {};

  for (const [type, instances] of Object.entries(byType)) {
    if (instances.length < 2) continue;
    const result = rationalizeGroup(instances, chordMap, lines, timingMap, beats);
    Object.assign(allCorrections, result.corrections);
    allAdditions.push(...result.additions);
    for (const k of result.deletions) allDeletions.add(k);
    diagnostics[type] = result.debug || null;
  }

  const numChanges = Object.keys(allCorrections).length
    + allAdditions.length
    + allDeletions.size;
  if (numChanges === 0) return { alignment, corrections: 0, diagnostics };

  let corrected = alignment
    .filter(entry => {
      if (entry.lineIndex < 0) return true;
      return !allDeletions.has(`${entry.lineIndex}:${entry.wordIndex}`);
    })
    .map(entry => {
      if (entry.lineIndex < 0) return entry;
      const key = `${entry.lineIndex}:${entry.wordIndex}`;
      if (allCorrections[key]) {
        return { ...entry, chord: allCorrections[key], rationalized: true };
      }
      return entry;
    });

  for (const { absLine, wordIndex, chord } of allAdditions) {
    const occupied = corrected.some(
      e => e.lineIndex === absLine && e.wordIndex === wordIndex
    );
    if (!occupied) {
      let wordTime = timingMap[absLine]?.[wordIndex]?.start ?? null;
      if (wordTime != null && (beats || []).length > 0) {
        // Placement policy: nearest beat grid slot.
        let nearest = beats[0];
        let minD = Math.abs(Number(beats[0].time) - Number(wordTime));
        for (const b of beats) {
          const d = Math.abs(Number(b.time) - Number(wordTime));
          if (d < minD) { minD = d; nearest = b; }
        }
        wordTime = Number(nearest.time);
      }
      const entry = { chord, lineIndex: absLine, wordIndex, rationalized: true };
      if (wordTime != null) entry.time = wordTime;
      corrected.push(entry);
    }
  }

  return { alignment: corrected, corrections: numChanges, diagnostics };
}
